/**
 * Rotation handle.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { TransformContext, KeyPoints } from "../types";
import { BaseHandle } from "./BaseHandle";
import {
  HandleType,
  HandlePosition,
  HANDLE_SIZES,
  TRANSFORM_THEME,
  HANDLE_OFFSET_PIXELS,
} from "../types";
import { MeasurementLabels } from "../../overlay/MeasurementLabels";

/**
 * Handle for rotating entities.
 */
export class RotationHandle extends BaseHandle {
  readonly type = HandleType.Rotation;
  readonly position = HandlePosition.Rotation;

  /** Start angle when rotation began */
  private startAngle = 0;

  private measureLabels: MeasurementLabels | null = null;

  private offsetInPx: number = HANDLE_OFFSET_PIXELS * HANDLE_SIZES.handleScale;
  private worldOffset: number;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    entityIds: DrawingModule.OdDbObjectId[],
    keyPoints: KeyPoints,
  ) {
    super(viewer, history, entityIds, keyPoints, HandlePosition.Rotation);

    // Convert fixed pixel distance to world units at current zoom
    this.worldOffset = this.viewer.screenDistanceToWorld(this.offsetInPx);

    // Position rotation handle centered above the grid at fixed pixel distance
    this.worldPosition = {
      x: keyPoints.center.x,
      y: keyPoints.max.y + this.worldOffset,
      z: keyPoints.center.z,
    };

    // Override draw options for rotation handle
    this.drawOptions = {
      fillColor: TRANSFORM_THEME.rotationHandle,
      strokeColor: TRANSFORM_THEME.handleStroke,
      radius: HANDLE_SIZES.rotation * HANDLE_SIZES.handleScale,
      icon: "rotate",
    };
  }

  /**
   * Start rotation - store initial angle.
   */
  start(): void {
    super.start();
    // Calculate initial angle from center to handle's actual visual position
    this.startAngle = Math.atan2(
      this.worldPosition.y - this.keyPoints.center.y,
      this.worldPosition.x - this.keyPoints.center.x,
    );
  }

  /**
   * Update ghost rotation during drag.
   */
  ongoing(context: TransformContext): void {
    if (!this.isTransforming) return;

    // Calculate current angle from center to current point
    const currentAngle = Math.atan2(
      context.worldPoint.y - this.keyPoints.center.y,
      context.worldPoint.x - this.keyPoints.center.x,
    );

    // Calculate rotation delta
    let deltaAngle = currentAngle - this.startAngle;

    // Snap to 15 degree increments if Shift is held
    if (context.shiftKey) {
      const snapIncrement = Math.PI / 12; // 15 degrees
      deltaAngle = Math.round(deltaAngle / snapIncrement) * snapIncrement;
    }

    // Update accumulated rotation
    this.setAccumulated({
      rotation: deltaAngle,
      rotationCenter: { ...this.keyPoints.center },
    });

    // Apply to all ghosts
    this.setGhostTransforms();

    // Update view with throttling for better performance
    this.throttledUpdate();

    this.updateMeasurements(deltaAngle);
  }

  private updateMeasurements(deltaAngle: number): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    if (!this.measureLabels) {
      this.measureLabels = new MeasurementLabels(
        overlay,
        this.viewer.getBackgroundColor(),
      );
    }

    const centerScreen = this.viewer.worldToScreen(this.keyPoints.center);
    const ARC_RADIUS = 40; // pixels

    // World angles use Y-up, but screen/canvas uses Y-down.
    // To convert: negate the angle so that directions match visually.
    const screenStart = -this.startAngle;
    const screenEnd = -(this.startAngle + deltaAngle);

    // Line from center to start direction
    const lineLen = ARC_RADIUS * 1.5;
    this.measureLabels.setLine(
      "startLine",
      centerScreen.x,
      centerScreen.y,
      centerScreen.x + Math.cos(screenStart) * lineLen,
      centerScreen.y + Math.sin(screenStart) * lineLen,
    );

    // Line from center to current direction
    this.measureLabels.setLine(
      "endLine",
      centerScreen.x,
      centerScreen.y,
      centerScreen.x + Math.cos(screenEnd) * lineLen,
      centerScreen.y + Math.sin(screenEnd) * lineLen,
    );

    // Arc between the two angles
    // Positive world delta = CCW in world = CCW visually.
    // With negated screen angles, screenEnd < screenStart when delta > 0,
    // so we need counterClockwise = true to sweep in the decreasing direction.
    this.measureLabels.setArc(
      "arc",
      centerScreen.x,
      centerScreen.y,
      ARC_RADIUS,
      screenStart,
      screenEnd,
      deltaAngle > 0,
    );

    // Angle label at the arc midpoint
    const screenMid = -(this.startAngle + deltaAngle / 2);
    const labelDist = ARC_RADIUS + 18;
    this.measureLabels.setLabel(
      "angle",
      centerScreen.x + Math.cos(screenMid) * labelDist,
      centerScreen.y + Math.sin(screenMid) * labelDist,
      MeasurementLabels.formatAngle(
        parseFloat((deltaAngle * (180 / Math.PI)).toFixed(1)),
      ),
    );
  }
  /** Use if redraw is called but handles are not recreated, must update offset */
  beforeRedraw(): void {
    const previousWorldOffset = this.worldOffset;
    this.worldOffset = this.viewer.screenDistanceToWorld(this.offsetInPx);
    this.worldPosition.y += this.worldOffset - previousWorldOffset;
  }

  end(): void {
    this.measureLabels?.clear();
    this.measureLabels = null;
    super.end();
  }

  cancel(): void {
    this.measureLabels?.clear();
    this.measureLabels = null;
    super.cancel();
  }
}
